import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, Users, MapPin, Heart, LogOut, Plane, CircleUser as UserCircle, Home, MessageSquare, Image, UserCheck, UserCog, X, Calculator, HeartHandshake, MessageCircle, Sparkles, MessagesSquare, GraduationCap, Mail, ClipboardList, ChevronDown, LayoutGrid, Send, Megaphone, CalendarPlus, Timer, FileText, Star, Repeat, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { UserRole } from '../types/auth';
import { permissionsApi } from '../lib/api';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [menuItems, setMenuItems] = React.useState<Array<{ icon: any; label: string; path: string; key: string; parentMenu: string | null }>>([]);
  const [openSubmenus, setOpenSubmenus] = React.useState<Record<string, boolean>>({});

  const menuItemIconPath: Record<string, { icon: any; path: string }> = {
    'Homepage': { icon: Home, path: '/dashboard' },
    'Bacheca': { icon: MessagesSquare, path: '/dashboard/board' },
    'Eventi': { icon: Calendar, path: '/dashboard/events' },
    'Pubblicità': { icon: Image, path: '/dashboard/advertisements' },
    'Invia Messaggi': { icon: MessageSquare, path: '/dashboard/send-messages' },
    'Clienti': { icon: Users, path: '/dashboard/clients' },
    'Matching': { icon: Heart, path: '/dashboard/matching' },
    'Location': { icon: MapPin, path: '/dashboard/locations' },
    'Team': { icon: UserCog, path: '/dashboard/team' },
    'Contabilità': { icon: Calculator, path: '/dashboard/contabilita' },
    'Agenzia Amore': { icon: HeartHandshake, path: '/dashboard/agenzia-amore' },
    'Dating Coach': { icon: MessageCircle, path: '/dashboard/dating-coach' },
    'Consulente d\'Immagine': { icon: Sparkles, path: '/dashboard/consulente-immagine' },
    'Messaggeria': { icon: Mail, path: '/dashboard/messaggeria' },
    'Tavoli': { icon: LayoutGrid, path: '/dashboard/messaggeria/tavoli' },
    'Messaggi': { icon: Send, path: '/dashboard/messaggeria/messaggi' },
    'Consulenze': { icon: ClipboardList, path: '/dashboard/consulenze' },
    'Corsi per Single': { icon: GraduationCap, path: '/dashboard/corsi-per-single' },
    'Viaggi per Single': { icon: Plane, path: '/dashboard/single-travel' },
    'Marketing': { icon: Megaphone, path: '/dashboard/marketing' },
    'Altri Eventi': { icon: CalendarPlus, path: '/dashboard/altri-eventi' },
    'Speed Date': { icon: Timer, path: '/dashboard/speed-date' },
    'Candidature': { icon: FileText, path: '/dashboard/candidature' },
    'Serate VIP': { icon: Star, path: '/dashboard/serate-vip' },
    'Serate Scambi': { icon: Repeat, path: '/dashboard/serate-scambi' },
    'Serate Esclusive': { icon: Crown, path: '' },
    'Gestione Staff': { icon: UserCheck, path: '/dashboard/user-management' },
  };

  React.useEffect(() => {
    if (user) {
      loadMenuItems();
    }
  }, [user]);

  React.useEffect(() => {
    const childItems = menuItems.filter(mi => mi.parentMenu);
    for (const child of childItems) {
      if (location.pathname === child.path) {
        setOpenSubmenus(prev => ({ ...prev, [child.parentMenu!]: true }));
      }
    }
  }, [location.pathname, menuItems]);

  const loadMenuItems = async () => {
    try {
      const permissions = await permissionsApi.getAll();

      const filteredItems = permissions
        .filter(permission => {
          const def = menuItemIconPath[permission.menu_item];
          if (!def) return false;

          switch (user!.role) {
            case 'admin': return permission.admin_access;
            case 'area_leader': return permission.area_leader_access;
            case 'organizer': return permission.organizer_access;
            case 'pr': return permission.pr_access;
            case 'collaboratore': return permission.collaboratore_access;
            default: return false;
          }
        })
        .map(permission => ({
          ...menuItemIconPath[permission.menu_item],
          label: permission.menu_item,
          key: permission.menu_item,
          parentMenu: permission.parent_menu || null,
        }));

      setMenuItems(filteredItems);
    } catch (error) {
      console.error('Error loading menu permissions:', error);
    }
  };

  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <div className={`
        fixed lg:static inset-y-0 left-0 z-50
        min-w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        flex flex-col overflow-hidden
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex items-center justify-between px-4 py-6 flex-shrink-0">
          <div
            onClick={() => handleNavigation('/dashboard')}
            className="flex items-center gap-2 cursor-pointer hover:text-rose-600 transition-colors"
          >
            <Heart className="h-8 w-8 text-rose-500" />
            <span className="text-xl font-bold">FlashCrush</span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label={t.sidebar.closeMenu}
          >
            <X className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4">
          <button
            onClick={() => handleNavigation('/dashboard/profile')}
            className="mb-6 px-4 py-2 w-full text-left rounded-lg hover:bg-rose-50 transition-colors group"
          >
            <div className="text-sm text-gray-600">{t.sidebar.welcome}</div>
            <div className="font-medium group-hover:text-rose-600 transition-colors">{user.name}</div>
            <div className="text-sm text-gray-500 capitalize">
              {user.role} {user.city && `(${user.city})`}
            </div>
          </button>

          <nav className="space-y-1 pb-6">
            {menuItems.filter(item => !item.parentMenu).map((item) => {
              const childItems = menuItems.filter(mi => mi.parentMenu === item.key);

              if (childItems.length > 0) {
                const childPaths = childItems.map(ci => ci.path);
                const isChildActive = childPaths.includes(location.pathname);
                const isOpen = openSubmenus[item.key] || false;

                return (
                  <div key={item.key}>
                    <button
                      onClick={() => setOpenSubmenus(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                      className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors ${
                        isChildActive
                          ? 'bg-rose-50 text-rose-600'
                          : 'text-gray-700 hover:bg-rose-50 hover:text-rose-600'
                      }`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="flex-1 text-left">{t.sidebar.menuItems[item.key] || item.label}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      {childItems.map((child) => (
                        <button
                          key={child.key}
                          onClick={() => handleNavigation(child.path)}
                          className={`flex items-center gap-3 w-full pl-8 pr-4 py-2.5 rounded-lg transition-colors ${
                            location.pathname === child.path
                              ? 'bg-rose-50 text-rose-600'
                              : 'text-gray-500 hover:bg-rose-50 hover:text-rose-600'
                          }`}
                        >
                          <child.icon className="h-4 w-4" />
                          <span className="text-sm">{t.sidebar.menuItems[child.key] || child.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <button
                  key={item.key}
                  onClick={() => handleNavigation(item.path)}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? 'bg-rose-50 text-rose-600'
                      : 'text-gray-700 hover:bg-rose-50 hover:text-rose-600'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{t.sidebar.menuItems[item.key] || item.label}</span>
                </button>
              );
            })}

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors text-gray-700 hover:bg-rose-50 hover:text-rose-600 mt-4"
            >
              <LogOut className="h-5 w-5" />
              <span>{t.sidebar.logout}</span>
            </button>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;